import React, { useEffect, useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  PanelLeftClose,
  Input,
  SearchIcon,
  X,
} from '@pagespeedify/ui';
import { Sparkles, Bot, ListTodo } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { UserType } from '../lib/auth/types';
import { ThemeToggle } from './ThemeToggle';
import UsageTabs from './UsageTabs';
import TaskList from './tasks/TaskList';
import { useApi } from '../lib/apiClient';
import { extractTasksArray } from '../lib/tasksResponse';
import { ApiResponse, Task } from '../types';
import { useSidebar } from '../context/SidebarContext';
import { SidebarFooter } from './SidebarFooter';
import { ContactFormModal } from './ContactFormModal';

const Sidebar: React.FC = () => {
  const { setIsSidebarOpen } = useSidebar();
  const { user, isAdmin } = useAuth();
  const apiClient = useApi();
  const navigate = useNavigate();
  const isFreeLikeUser = (user?.user_type || '').toLowerCase() === 'unsubscribed' || (user?.user_type || '').toLowerCase() === 'free';
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'agent' | 'tasks'>(isFreeLikeUser ? 'tasks' : 'agent');
  const [taskTypeFilter, setTaskTypeFilter] = useState<string>('all');
  const [isContactModalOpen, setContactModalOpen] = useState(false);
  const tasksPerPage = 20;

  useEffect(() => {
    if (isFreeLikeUser && activeTab === 'agent') {
      setActiveTab('tasks');
    }
  }, [activeTab, isFreeLikeUser]);

  const {
    data: tasksResponse,
    isLoading: isLoadingTasks,
    isError: isTasksError,
    error: tasksFetchError,
  } = useQuery<ApiResponse<any>, Error>({
    queryKey: ['ai-tasks-list'],
    queryFn: async () => apiClient.get('/tasks'),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const displayTasks = extractTasksArray(tasksResponse);
  const usage = tasksResponse?.data?.tasks?.usage;

  // Filter tasks based on active tab
  const filteredTasks = displayTasks.filter((task: Task) => {
    const titleMatch = (task.title ?? '').toLowerCase().includes(searchTerm.toLowerCase());

    if (activeTab === 'agent') {
      // Agent tab: Only show agent-type parent tasks
      return titleMatch && task.type === 'agent' && !task.parent_task_id;
    } else {
      // Tasks tab: Show all other task types with optional filter
      const isNotAgent = task.type !== 'agent';
      const typeMatch = taskTypeFilter === 'all' || task.type === taskTypeFilter;
      return titleMatch && isNotAgent && typeMatch;
    }
  });

  const paginatedTasks = filteredTasks.slice(
    (currentPage - 1) * tasksPerPage,
    currentPage * tasksPerPage
  );
  const totalPages = Math.ceil(filteredTasks.length / tasksPerPage);

  const getNavLinkClass = ({ isActive }: { isActive: boolean }): string => {
    return isActive ? 'font-bold text-primary' : 'text-foreground hover:text-primary/80';
  };

  // Helper to detect mobile (screen width < 768px)
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

  const handleTabChange = (value: string) => {
    if (isFreeLikeUser && value === 'agent') return;
    setActiveTab(value as 'agent' | 'tasks');
    setCurrentPage(1); // Reset pagination when switching tabs
    setSearchTerm(''); // Clear search when switching tabs
  };

  return (
    <>
      <ContactFormModal isOpen={isContactModalOpen} onClose={() => setContactModalOpen(false)} />
      <div className="flex flex-col h-full bg-background text-foreground border-r border-border inset-0 z-[9998]">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <Link to="/" className="text-lg font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent no-underline hover:opacity-80 transition-opacity">
                Swayashin
              </Link>
              {isAdmin && (
                <span className="text-xs font-medium px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                  Admin
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button variant="ghost" size="icon" onClick={() => setIsSidebarOpen(false)} className="h-7 w-7 md:hidden">
                <X className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setIsSidebarOpen(false)} className="h-7 w-7 hidden md:flex">
                <PanelLeftClose className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-grow p-4 overflow-y-auto no-scrollbar">
          {/* Agent / Tasks Tabs */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-4">
            <TabsList className={`grid w-full ${isFreeLikeUser ? 'grid-cols-1' : 'grid-cols-2'}`}>
              {!isFreeLikeUser && (
                <TabsTrigger value="agent" className="flex items-center gap-1.5">
                  <Bot className="h-4 w-4" />
                  Agent
                </TabsTrigger>
              )}
              <TabsTrigger value="tasks" className="flex items-center gap-1.5">
                <ListTodo className="h-4 w-4" />
                Tasks
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Search */}
          <div className="relative mb-4">
            <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={activeTab === 'agent' ? "Search agent tasks..." : "Search tasks..."}
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Filter (only for Tasks tab) */}
          {activeTab === 'tasks' && (
            <div className="relative mb-4">
              <Select value={taskTypeFilter} onValueChange={setTaskTypeFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Filter by type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="monitoring">Monitoring</SelectItem>
                  <SelectItem value="image_optimization">Image Optimization</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Task List - for both tabs */}
          <div className="mb-4" data-tour="tasks-sidebar">
            <TaskList
              tasks={paginatedTasks}
              isLoading={isLoadingTasks}
              isError={isTasksError}
              error={tasksFetchError}
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
              usage={usage}
            />
            {filteredTasks.length === 0 && !isLoadingTasks && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                {activeTab === 'agent' ? (
                  <div>
                    <Bot className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No agent tasks yet</p>
                    <Button
                      variant="link"
                      className="text-primary h-auto p-0 mt-1"
                      onClick={() => { if (isMobile) setIsSidebarOpen(false); navigate('/agent'); }}
                    >
                      Start a new agent task →
                    </Button>
                  </div>
                ) : (
                  <p>No tasks found</p>
                )}
              </div>
            )}
          </div>

          <Accordion type="single" collapsible>
            <AccordionItem value="tools" className="border-none" data-tour="tools-section">
              <AccordionTrigger>Tools</AccordionTrigger>
              <AccordionContent>
                <ul className="list-none md:text-sm p-0 mt-2 flex flex-col gap-y-2">
                  <li data-tour="reports-page-link"><NavLink to="/reports" className={getNavLinkClass} onClick={() => { if (isMobile) setIsSidebarOpen(false); }}>Reports</NavLink></li>
                  <li><NavLink to="/monitoring" className={getNavLinkClass} onClick={() => { if (isMobile) setIsSidebarOpen(false); }}>Monitoring</NavLink></li>
                  <li><NavLink to="/image-optimization" className={getNavLinkClass} onClick={() => { if (isMobile) setIsSidebarOpen(false); }}>Image Optimization</NavLink></li>
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="manage" className="border-none">
              <AccordionTrigger>Manage</AccordionTrigger>
              <AccordionContent>
                <ul className="list-none md:text-sm p-0 mt-2 flex flex-col gap-y-2">
                  {user?.user_type === UserType.ENTERPRISE && (
                    <li><NavLink to="/manage-team" className={getNavLinkClass} onClick={() => { if (isMobile) setIsSidebarOpen(false); }}>Team</NavLink></li>
                  )}
                  <li><NavLink to="/subscriptions" className={getNavLinkClass} onClick={() => { if (isMobile) setIsSidebarOpen(false); }}>Subscriptions</NavLink></li>
                  <li><NavLink to="/credits" className={getNavLinkClass} onClick={() => { if (isMobile) setIsSidebarOpen(false); }}>Credits</NavLink></li>
                  <li><NavLink to="/notifications" className={getNavLinkClass} onClick={() => { if (isMobile) setIsSidebarOpen(false); }}>Notifications</NavLink></li>
                  {!isFreeLikeUser && (
                    <li><NavLink to="/agent/settings" className={getNavLinkClass} onClick={() => { if (isMobile) setIsSidebarOpen(false); }}>Agent Settings</NavLink></li>
                  )}
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <div className="p-2 border-t border-border">
          <div className="flex items-center justify-between">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="usage" className="border-none" data-tour="usage-section">
                <AccordionTrigger className="py-1 text-sm">Usage</AccordionTrigger>
                <AccordionContent>
                  <UsageTabs />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        <SidebarFooter onContactClick={() => setContactModalOpen(true)} />
      </div>
    </>
  );
};

export default Sidebar;
